import { EDITOR_ACTIONS } from '~/constants/documentActions';
import { editableFields } from '~/constants/documentEditor';

import { encodeToBase64 } from '~/utils/message';

const getPath = (obj) => obj?.cssPath || obj?.id || '';

const getParentChildrenPairsFromLayout = (elements) => {
  if (!Array.isArray(elements) || elements.length === 0) {
    return { parent: null, children: [] };
  }

  const validElements = elements.filter((el) => getPath(el) !== '');

  // Find a parent that is prefix of at least one other element
  const parents = validElements.filter((p) =>
    validElements.some((c) => c !== p && getPath(c).startsWith(getPath(p) + '>')),
  );

  if (parents.length === 0) {
    return { parent: null, children: [] };
  }

  // Pick the deepest parent (most specific)
  const parent = parents.sort((a, b) => getPath(b).length - getPath(a).length)[0];

  // All descendants: starts with parent path
  const children = validElements.filter((el) => el !== parent && getPath(el).startsWith(getPath(parent) + '>'));

  return { parent, children };
};

const createField = (field, action, parentLayout = null) => {
  const locked = parentLayout?.attributes?.['data-ant-locked'] === 'true';
  const required = !!field?.attributes?.['required'];
  let visible = !field?.attributes?.['hidden'];

  if (parentLayout) {
    const { parent, children } = getParentChildrenPairsFromLayout(parentLayout.children);
    const visibilityFalseFromParent =
      parent?.attributes?.['hidden'] && children?.find((item) => item.cssPath === field.cssPath);
    if (visibilityFalseFromParent) {
      visible = false;
    }
  }

  return {
    visible,
    required,
    id: field.cssPath,
    value: encodeToBase64(field.element.text),
    fieldName: field.attributes?.fieldName,
    objectType: field.element.tag,
    updated: action === EDITOR_ACTIONS.selectElement || action === EDITOR_ACTIONS.selectLayout ? true : !locked,
  };
};

const mapLayoutChildrenElements = (layout, action, selectedElement = null) => {
  if (selectedElement?.children?.length) {
    return selectedElement.children?.map((child) => createField(child, action, layout));
  }
  return selectedElement
    ? [createField(selectedElement, action)]
    : layout.children?.map((child) => createField(child, action, layout));
};

export const createLayout = (selectedLayout, action, selectedField) => ({
  id: selectedLayout.cssPath,
  fieldName: selectedLayout.attributes?.fieldName,
  layoutName: selectedLayout.attributes?.['layout-name'],
  locked: selectedLayout.attributes?.['data-ant-locked'] === 'true', // field for locked layout
  fields: mapLayoutChildrenElements(selectedLayout, action, selectedField),
});

export const isImage = ({ element, fieldFromLLM }) =>
  element?.tag?.includes('image') ||
  fieldFromLLM?.field_name?.toLowerCase().includes('image') ||
  fieldFromLLM?.field_name?.toLowerCase().includes('artwork');

export const isImageUrl = (url) => url.includes('veeva/') || url.includes('s3/');

export const isEditableField = (fieldType) => editableFields.includes(fieldType);

export const extractImageExternalId = (url) => {
  const match = url.match(/images\/(veeva\/\d+\/[\w\-.]+)/) || url.match(/images\/(s3\/[\w\-.\/]+)/);
  return match ? match[1] : null;
};

export const stripOldWesleyImports = (contentString) => {
  const scriptRgx = new RegExp(
    `<script\\b[^<]*src="[^"]*\/web-editor\/[^"]*"[^<]*?(?:(?!<\/script>)<[^<]*)*<\/scripts*>`,
    'gmi',
  );
  const styleRgx = new RegExp(`<link\\b[^>]*?href="[^\"]*\/web-editor\/[^"]*"[^>]*?>`, 'gmi');
  return contentString.replace(new RegExp(`${scriptRgx.source}|${styleRgx.source}`, 'gmi'), '');
};

export const mapEditorIdsToLLMIds = (fieldId, mapping) => {
  // Step 1: replace "body" with "mj-body" only at the start
  fieldId = fieldId.replace(/^body/, 'mj-body');

  // Step 2: replace IDs with mapping values if they exist
  return fieldId.replace(/#([A-Z0-9_]+)/g, (match, id) => {
    return '#' + (mapping[id] || id);
  });
};

export const mapTacticToTacticType = (tacticTypes) => {
  return tacticTypes.reduce((acc, type) => {
    if (type?.id) {
      return { ...acc, [type.id]: type.tacticContexts.map((item) => ({ ...item, regionDescriptionName: type.title })) };
    }
    return acc;
  }, {});
};
